import type { WebGpuCanvasHandle } from "../webgpu-canvas";

export interface TimelinePipeline {
  pipeline: GPURenderPipeline;
  uniformBuffer: GPUBuffer;
  vertexBuffer: GPUBuffer;
  bindGroup: GPUBindGroup;
}

export const MAX_VERTS = 48_000;
export const FLOATS_PER_VERT = 6;

export function createTimelinePipeline(
  handle: WebGpuCanvasHandle,
  shaderSource: string,
): TimelinePipeline {
  const { device, format } = handle;
  const module = device.createShaderModule({
    label: "timeline-shader",
    code: shaderSource,
  });

  const uniformBuffer = device.createBuffer({
    label: "timeline-uniforms",
    size: 8,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const vertexBuffer = device.createBuffer({
    label: "timeline-vertices",
    size: MAX_VERTS * FLOATS_PER_VERT * 4,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
  });

  const bindGroupLayout = device.createBindGroupLayout({
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.VERTEX,
        buffer: { type: "uniform" },
      },
    ],
  });

  const bindGroup = device.createBindGroup({
    layout: bindGroupLayout,
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });

  const pipelineLayout = device.createPipelineLayout({
    bindGroupLayouts: [bindGroupLayout],
  });

  const pipeline = device.createRenderPipeline({
    label: "timeline-pipeline",
    layout: pipelineLayout,
    vertex: {
      module,
      entryPoint: "vs_main",
      buffers: [
        {
          arrayStride: FLOATS_PER_VERT * 4,
          attributes: [
            { shaderLocation: 0, offset: 0, format: "float32x2" },
            { shaderLocation: 1, offset: 8, format: "float32x4" },
          ],
        },
      ],
    },
    fragment: {
      module,
      entryPoint: "fs_main",
      targets: [{ format }],
    },
    primitive: { topology: "triangle-list" },
  });

  return { pipeline, uniformBuffer, vertexBuffer, bindGroup };
}
